import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useStudents } from 'context/StudentsContext';
import {
  CANONICAL_FIELDS,
  parseCsvText,
  buildInitialMapping,
  rowToStudentDraft,
  validateDraft,
  findLikelyDuplicates,
  buildCsvTemplate,
} from '../../lib/csvRoster';

// ---------------------------------------------------------------------------
// Import roster (CSV) — /students/import
//
// A four-step wizard that lets a teacher (or admin) load a caseload from
// a spreadsheet exported by their SIS / IEP-management platform. This
// exists because until now the ONLY way to add students was one at a
// time through the Add Student modal — a district asking "does it pull
// from PowerSchool / EdPlan?" wanted a single button, and CSV is the
// lowest-common-denominator that every SIS supports.
//
// Steps:
//
//   1. Upload — file picker + drag/drop, plus a "Download template" so
//      the teacher can hand it to her district's data coordinator and
//      say "fill this in."
//
//   2. Map columns — auto-suggests mappings for each CSV header,
//      teacher can override any. First 3 preview rows update live so
//      she sees what her mapping is doing to the data.
//
//   3. Preview + validate — every row is turned into a normalized
//      student draft, validated (First / Last required, other fields
//      warned but not blocked), and compared to the existing roster for
//      probable duplicates. Bad rows are called out inline; the teacher
//      picks which to skip.
//
//   4. Import — creates students one at a time via addStudent() so we
//      inherit the same schema-degradation retry loop that AddStudent
//      already has (migration 0008/0010 columns are stripped and retried
//      if the district's Supabase is behind). Shows a per-row progress
//      indicator so she can see when it's done.
//
// After success we route back to /student-profile-management so the
// full imported roster is right in front of her.
// ---------------------------------------------------------------------------

const STEPS = [
  { key: 'upload',  label: '1. Upload' },
  { key: 'map',     label: '2. Map columns' },
  { key: 'preview', label: '3. Preview' },
  { key: 'import',  label: '4. Import' },
];

function downloadFile(filename, content, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const StudentsImportPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { students, addStudent, isLive } = useStudents();

  const [step, setStep] = useState('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [mapping, setMapping] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Per-row decisions the teacher makes on the Preview screen.
  // rowIndex -> 'import' | 'skip'.
  const [rowDecisions, setRowDecisions] = useState({});

  // Import progress state.
  const [importLog, setImportLog] = useState([]); // [{rowIndex, name, status, error}]
  const [isImporting, setIsImporting] = useState(false);

  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setParseError(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setParseError('Couldn\'t read that file — is it definitely a .csv?');
    };
    reader.onload = () => {
      const text = reader.result;
      if (typeof text !== 'string') {
        setParseError('Couldn\'t read that file — is it definitely a .csv?');
        return;
      }
      const parsed = parseCsvText(text);
      if (parsed.rows.length === 0) {
        setParseError('The file didn\'t contain any student rows.');
        return;
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(buildInitialMapping(parsed.headers));
      setRowDecisions({});
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const drafts = useMemo(() => {
    return rows.map((row) => {
      const draft = rowToStudentDraft(row, mapping);
      const validation = validateDraft(draft);
      const duplicates = findLikelyDuplicates(draft, students);
      return {
        rowIndex: row.__rowIndex,
        draft,
        validation,
        duplicates,
      };
    });
  }, [rows, mapping, students]);

  const previewRows = useMemo(() => drafts.slice(0, 3), [drafts]);

  const importCounts = useMemo(() => {
    let good = 0;
    let badRequired = 0;
    let likelyDup = 0;
    let warned = 0;
    for (const d of drafts) {
      if (!d.validation.valid) badRequired += 1;
      else if (d.duplicates.length > 0) likelyDup += 1;
      else if (d.validation.warnings.length > 0) warned += 1;
      else good += 1;
    }
    return { good, badRequired, likelyDup, warned, total: drafts.length };
  }, [drafts]);

  // For each row, resolve what happens on Import:
  //   * Errors present -> always skipped
  //   * Teacher explicit skip -> skipped
  //   * Otherwise -> imported
  const decidedRows = useMemo(() => {
    return drafts.map((d) => {
      let action = 'import';
      if (!d.validation.valid) action = 'skip';
      else if (rowDecisions[d.rowIndex] === 'skip') action = 'skip';
      return { ...d, action };
    });
  }, [drafts, rowDecisions]);

  const toImportCount = decidedRows.filter((r) => r.action === 'import').length;

  const canGoToPreview = useMemo(() => {
    const mappedFields = new Set(
      Object.values(mapping).filter((v) => v && v !== '__ignore__'),
    );
    return mappedFields.has('firstName') && mappedFields.has('lastName');
  }, [mapping]);

  const handleRunImport = useCallback(async () => {
    if (isImporting) return;
    setIsImporting(true);
    setStep('import');
    const importable = decidedRows.filter((r) => r.action === 'import');
    const log = [];
    for (const item of importable) {
      const name = `${item.draft.firstName} ${item.draft.lastName}`.trim();
      try {
        await addStudent(item.draft);
        log.push({
          rowIndex: item.rowIndex,
          name,
          status: 'added',
        });
      } catch (err) {
        log.push({
          rowIndex: item.rowIndex,
          name,
          status: 'error',
          error: err?.message || 'Unknown error',
        });
      }
      setImportLog([...log]);
    }
    setIsImporting(false);
  }, [addStudent, decidedRows, isImporting]);

  const successCount = importLog.filter((r) => r.status === 'added').length;
  const errorCount = importLog.filter((r) => r.status === 'error').length;

  const handleStartOver = () => {
    setStep('upload');
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setRowDecisions({});
    setImportLog([]);
    setParseError(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-4 sm:p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <Breadcrumb
              customItems={[
                { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                { label: 'Students', path: '/student-profile-management', icon: 'Users' },
                { label: 'Import from spreadsheet', path: null, icon: 'Upload', current: true },
              ]}
            />

            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Import roster from a spreadsheet
              </h1>
              <p className="text-sm text-muted-foreground max-w-3xl mt-1">
                Upload a CSV exported from your SIS or IEP-management
                platform (PowerSchool, EdPlan, Skyward, Infinite Campus,
                Aeries — anything that exports a spreadsheet). The
                app will match up the columns for you, flag any missing
                information, and add every student to your roster in one
                step.
              </p>
              {!isLive && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 max-w-3xl">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                    <p>
                      You're in demo mode. Importing here will fill your
                      demo roster on this browser only — nothing is saved
                      to a real database.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Step strip */}
            <div className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="flex overflow-x-auto">
                {STEPS.map((s) => {
                  const isActive = s.key === step;
                  return (
                    <div
                      key={s.key}
                      className={`flex-1 min-w-[140px] px-4 py-3 border-b-2 text-sm font-medium ${
                        isActive
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-transparent text-muted-foreground'
                      }`}
                    >
                      {s.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* STEP: UPLOAD -------------------------------------------- */}
            {step === 'upload' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={onDrop}
                  className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background'
                  }`}
                >
                  <Icon name="Upload" size={32} className="mx-auto text-primary mb-3" />
                  <p className="text-base font-medium text-foreground">
                    Drop a .csv file here, or pick one from your computer
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Excel, Google Sheets, Numbers — any of them can export CSV.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      onChange={onFileInputChange}
                      className="hidden"
                    />
                    <Button
                      variant="default"
                      onClick={() => fileInputRef.current?.click()}
                      className="min-h-12"
                    >
                      <Icon name="FolderOpen" size={16} />
                      Pick a CSV file
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => downloadFile('edutracker-roster-template.csv', buildCsvTemplate())}
                      className="min-h-12"
                      title="Download a starter CSV to fill in"
                    >
                      <Icon name="Download" size={16} />
                      Download template
                    </Button>
                  </div>
                </div>

                {parseError && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-foreground">
                          Couldn't read the file
                        </p>
                        <p className="text-muted-foreground mt-1">{parseError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">
                        What should be in the CSV?
                      </p>
                      <p className="mt-1">
                        At minimum, two columns: first name and last
                        name. Everything else is optional — date of
                        birth, grade, primary disability, IEP dates,
                        notes. If your SIS uses different column names
                        ("StudentFirstName", "DOB", "IDEA Category"),
                        we'll try to auto-match them for you and let
                        you override on the next screen.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* STEP: MAP ---------------------------------------------- */}
            {step === 'map' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      Match up the columns
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-2xl">
                      For each column from your spreadsheet, tell the app
                      which student field it should go into. First name
                      and last name are required — everything else is
                      optional and can be left as "Ignore."
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    <Icon name="File" size={12} className="inline mr-1" />
                    {fileName || 'roster.csv'} — {rows.length} rows
                  </span>
                </div>

                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2 w-1/3">Column from your file</th>
                        <th className="text-left px-3 py-2 w-1/3">Maps to</th>
                        <th className="text-left px-3 py-2">Preview (first row)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers.map((h) => {
                        const currentValue = mapping[h] || '__ignore__';
                        const firstPreview = rows[0]?.[h] || '';
                        return (
                          <tr key={h} className="border-t border-border">
                            <td className="px-3 py-2 font-medium text-card-foreground">
                              {h || <span className="italic text-muted-foreground">(blank header)</span>}
                            </td>
                            <td className="px-3 py-2">
                              <Select
                                options={CANONICAL_FIELDS.map((f) => ({
                                  value: f.key,
                                  label: f.label + (f.required ? ' *' : ''),
                                }))}
                                value={currentValue}
                                onChange={(v) => setMapping((prev) => ({ ...prev, [h]: v }))}
                              />
                            </td>
                            <td className="px-3 py-2 text-muted-foreground truncate max-w-xs">
                              {firstPreview || <span className="italic">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!canGoToPreview && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                      <p>
                        You need to map at least one column to
                        <strong> First name</strong> and one to
                        <strong> Last name</strong> before you can continue.
                      </p>
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Icon name="Eye" size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Live preview</p>
                      <p className="mt-1">
                        Here's how the first {previewRows.length} rows will look
                        after mapping:
                      </p>
                      <ul className="mt-2 space-y-1">
                        {previewRows.map((p) => (
                          <li key={p.rowIndex} className="text-sm">
                            <span className="font-medium text-foreground">
                              {p.draft.firstName || '—'} {p.draft.lastName || '—'}
                            </span>{' '}
                            <span className="text-muted-foreground">
                              {p.draft.grade && `· grade ${p.draft.grade}`}
                              {p.draft.dateOfBirth && ` · DOB ${p.draft.dateOfBirth}`}
                              {p.draft.primaryDisability && ` · ${p.draft.primaryDisability}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep('upload')}>
                    <Icon name="ArrowLeft" size={14} />
                    Back
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setStep('preview')}
                    disabled={!canGoToPreview}
                    className="min-h-12"
                  >
                    Continue
                    <Icon name="ArrowRight" size={14} />
                  </Button>
                </div>
              </section>
            )}

            {/* STEP: PREVIEW ----------------------------------------- */}
            {step === 'preview' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Review before you import
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {toImportCount} of {importCounts.total} rows will be added.
                    Rows with missing names are always skipped. Possible
                    duplicates are pre-flagged — you decide whether to add
                    them anyway.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <SummaryTile label="Ready to add" count={importCounts.good} tone="ok" />
                  <SummaryTile label="Likely duplicates" count={importCounts.likelyDup} tone="warn" />
                  <SummaryTile label="Rows with warnings" count={importCounts.warned} tone="warn" />
                  <SummaryTile label="Missing name (skip)" count={importCounts.badRequired} tone="bad" />
                </div>

                <div className="rounded-md border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Grade · DOB · Disability</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decidedRows.map((r) => {
                        const isSkipped = r.action === 'skip';
                        return (
                          <tr key={r.rowIndex} className={`border-t border-border ${isSkipped ? 'opacity-60' : ''}`}>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              #{r.rowIndex}
                            </td>
                            <td className="px-3 py-2 font-medium text-card-foreground">
                              {r.draft.firstName || <span className="text-muted-foreground italic">missing</span>}{' '}
                              {r.draft.lastName || <span className="text-muted-foreground italic">missing</span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {[r.draft.grade, r.draft.dateOfBirth, r.draft.primaryDisability]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </td>
                            <td className="px-3 py-2">
                              {r.validation.errors.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                  <Icon name="AlertCircle" size={12} />
                                  {r.validation.errors[0]}
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                                  <Icon name="AlertTriangle" size={12} />
                                  Looks like {r.duplicates[0].existingName}
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length === 0 &&
                                r.validation.warnings.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                                  <Icon name="AlertTriangle" size={12} />
                                  {r.validation.warnings[0]}
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length === 0 &&
                                r.validation.warnings.length === 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                  <Icon name="Check" size={12} />
                                  Ready
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.validation.errors.length > 0 ? (
                                <span className="text-xs text-muted-foreground italic">
                                  will skip
                                </span>
                              ) : (
                                <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={r.action === 'import'}
                                    onChange={(e) =>
                                      setRowDecisions((prev) => ({
                                        ...prev,
                                        [r.rowIndex]: e.target.checked ? 'import' : 'skip',
                                      }))
                                    }
                                  />
                                  Add
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep('map')}>
                    <Icon name="ArrowLeft" size={14} />
                    Back
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleRunImport}
                    disabled={toImportCount === 0}
                    className="min-h-12"
                  >
                    <Icon name="Upload" size={14} />
                    Add {toImportCount} student{toImportCount === 1 ? '' : 's'}
                  </Button>
                </div>
              </section>
            )}

            {/* STEP: IMPORT ------------------------------------------ */}
            {step === 'import' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {isImporting ? 'Adding students…' : 'Done'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {isImporting
                      ? `Adding ${toImportCount} students. Please leave this page open.`
                      : errorCount === 0
                        ? `Added ${successCount} students to your roster.`
                        : `Added ${successCount} students. ${errorCount} row${errorCount === 1 ? '' : 's'} failed — see below.`}
                  </p>
                </div>

                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Student</th>
                        <th className="text-left px-3 py-2">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importLog.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground text-sm">
                            <Icon name="Loader2" size={16} className="inline animate-spin mr-2" />
                            Starting…
                          </td>
                        </tr>
                      )}
                      {importLog.map((entry) => (
                        <tr key={entry.rowIndex} className="border-t border-border">
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            #{entry.rowIndex}
                          </td>
                          <td className="px-3 py-2 font-medium">{entry.name}</td>
                          <td className="px-3 py-2">
                            {entry.status === 'added' && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                <Icon name="Check" size={12} />
                                Added
                              </span>
                            )}
                            {entry.status === 'error' && (
                              <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                <Icon name="AlertCircle" size={12} />
                                {entry.error || 'Error'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!isImporting && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Button variant="outline" onClick={handleStartOver}>
                      <Icon name="RotateCcw" size={14} />
                      Import another spreadsheet
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => navigate('/student-profile-management')}
                      className="min-h-12"
                    >
                      <Icon name="Users" size={14} />
                      See my roster
                    </Button>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const SummaryTile = ({ label, count, tone }) => {
  const toneClass =
    tone === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : tone === 'bad'
        ? 'border-red-200 bg-red-50 text-red-900'
        : 'border-amber-200 bg-amber-50 text-amber-900';
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
};

export default StudentsImportPage;
